import { useEffect, useState } from "preact/hooks";
import { ApiError, api, type MessageDetail, type ThreadMessage } from "./api";
import { Link } from "./app";
import { fileType, formatAddress, formatBytes, formatDateTime } from "./format";

const STATUS_TEXT: Record<string, string> = {
  indexed: "text indexed",
  empty: "no text found",
  skipped_type: "not indexed (file type)",
  skipped_size: "not indexed (over 20 MB)",
  error: "could not read",
};

export function MessageView({ id }: { id: string }) {
  const [message, setMessage] = useState<MessageDetail | null>(null);
  const [thread, setThread] = useState<ThreadMessage[] | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setMessage(null);
    setThread(null);
    setError("");
    api
      .message(id)
      .then((r) => setMessage(r.message))
      .catch((err: unknown) =>
        setError(err instanceof ApiError && err.status === 404 ? "Message not found in your index." : String((err as Error).message)),
      );
    api
      .thread(id)
      .then((r) => setThread(r.thread.messages))
      .catch(() => setThread([]));
    scrollTo(0, 0);
  }, [id]);

  return (
    <main>
      <p>
        <Link href="/">← Back</Link>
      </p>
      {error && <p class="notice danger">{error}</p>}
      {!message && !error && <p class="muted">Loading…</p>}
      {message && (
        <>
          <section class="card message-head">
            <h1>{message.subject}</h1>
            <dl>
              <dt>From</dt>
              <dd>{formatAddress(message.from)}</dd>
              {message.to.length > 0 && (
                <>
                  <dt>To</dt>
                  <dd>{message.to.map(formatAddress).join(", ")}</dd>
                </>
              )}
              {message.cc.length > 0 && (
                <>
                  <dt>Cc</dt>
                  <dd>{message.cc.map(formatAddress).join(", ")}</dd>
                </>
              )}
              <dt>Date</dt>
              <dd>{formatDateTime(message.date)}</dd>
              <dt>Folder</dt>
              <dd>
                {message.folders
                  .map((f) => `${f.name}${f.flags.some((x) => x.toLowerCase() === "\\seen") ? "" : " (unread)"}`)
                  .join(", ")}
              </dd>
            </dl>
          </section>
          <section class="card">
            {message.partial && (
              <p class="notice warn">
                This message is over 25 MB. Its text and readable attachments were indexed part by part; other large attachments were
                skipped and the full original was not stored.
              </p>
            )}
            <div class="body">{message.body || <span class="muted">(no text body)</span>}</div>
          </section>
          {message.attachments.length > 0 && (
            <section class="card">
              <h2>Attachments</h2>
              {message.attachments.map((a) => (
                <div class="attachment" key={a.index}>
                  <div class="row spread">
                    <strong>{a.filename || "(unnamed)"}</strong>
                    <span class="row small">
                      <span class="muted">
                        {fileType(a.filename, a.mime)} · {formatBytes(a.size)} · {STATUS_TEXT[a.status] ?? a.status}
                      </span>
                      {!message.partial && (
                        <a class="button" href={`/api/messages/${message.id}/attachments/${a.index}`} download>
                          Download
                        </a>
                      )}
                    </span>
                  </div>
                  {a.text && (
                    <details>
                      <summary>Extracted text</summary>
                      <pre>{a.text}</pre>
                    </details>
                  )}
                </div>
              ))}
            </section>
          )}
          {thread && thread.length > 1 && (
            <section class="card">
              <h2>Conversation</h2>
              <p class="sub">{thread.length} messages, oldest first, across all folders.</p>
              <ul class="results">
                {thread.map((t) => (
                  <li key={t.id}>
                    {t.id === message.id ? (
                      <strong>{t.subject}</strong>
                    ) : (
                      <Link href={`/m/${t.id}`} class="subject">
                        {t.subject}
                      </Link>
                    )}
                    {t.id === message.id && <span class="badge" style={{ marginLeft: "8px" }}>this message</span>}
                    {t.unread && <span class="badge" style={{ marginLeft: "8px" }}>unread</span>}
                    <div class="meta">
                      {formatAddress(t.from)} · {formatDateTime(t.date)} · {t.folders.join(", ")}
                    </div>
                    <p class="snippet">{t.body.length > 280 ? `${t.body.slice(0, 280)}…` : t.body}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </main>
  );
}
